import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsConstants from "../../common/AccountsConstants";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import { formatINR, ledgerIdOf, todayInput } from "../accounts/accountUser";
import LedgerCombo from "../accounts/LedgerCombo";
import { ApiAlert } from "../accounts/AccountWidgets";
import PageShell from "../accounts/PageShell";
import useAccountMeta from "../accounts/useAccountMeta";
import CollapsibleSection from "../../Components/CollapsibleSection/CollapsibleSection";
import EntryPreview from "../../Components/Accounts/EntryPreview";
import "../accounts/accounts.css";

/*
 * Credit note, debit note, journal and purchase bill for the existing menus.
 * Same ledgers and Dr/Cr sides as the Accounts module entry screens; posted
 * through /api/accounts-flow so the customer's order credit moves with it.
 */

const CASH_BANK = ["BANK", "CASH"];

const CONFIG = {
  CREDIT_NOTE: {
    title: "Credit Note",
    hint: "Reduces what the customer owes (returns, discounts). Frees order credit.",
    debit: { label: "Sales / Discount Ledger (Dr)", groups: ["INCOME", "EXPENSE"] },
    credit: { label: "Customer Ledger (Cr)", groups: ["SUNDRY_DEBTOR"] },
  },
  DEBIT_NOTE: {
    title: "Debit Note",
    hint: "Adds to what the customer owes (extra charges). Uses order credit.",
    debit: { label: "Customer Ledger (Dr)", groups: ["SUNDRY_DEBTOR"] },
    credit: { label: "Income Ledger (Cr)", groups: ["INCOME"] },
  },
  JOURNAL: {
    title: "Journal Voucher",
    hint: "Transfer between two ledgers (no cash or bank).",
    debit: { label: "Debit Ledger (Dr)", exclude: CASH_BANK },
    credit: { label: "Credit Ledger (Cr)", exclude: CASH_BANK },
  },
  PURCHASE: {
    title: "Purchase Bill",
    hint: "What the company owes the supplier. Pay it from Supplier Payment.",
    debit: { label: "Purchase / Expense Ledger (Dr)", groups: ["EXPENSE", "ASSET"] },
    credit: { label: "Supplier Ledger (Cr)", groups: ["SUNDRY_CREDITOR"] },
  },
};

const amountOk = (text) => /^\d+(\.\d{1,2})?$/.test(String(text).trim()) && Number(text) > 0;
const fits = (ledger, side) => (side.groups ? side.groups.includes(ledger.group) : !side.exclude.includes(ledger.group));

export default function FlowAdjustmentEntry({ voucherType }) {
  const navigate = useNavigate();
  const config = CONFIG[voucherType];
  const { meta } = useAccountMeta();
  const [flowMeta, setFlowMeta] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const blank = () => ({ voucherDate: todayInput(), referenceNo: "", narration: "", debitLedgerId: "", creditLedgerId: "", amount: "" });
  const [form, setForm] = useState(blank);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    accountsFlowApi.meta().then((result) => setFlowMeta(result?.data || null)).catch((err) => setError(err));
    accountsApi.ledgers({ isActive: "true" }).then((result) => setLedgers(result?.data || [])).catch((err) => setError(err));
  }, []);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const byId = useMemo(() => new Map(ledgers.map((row) => [ledgerIdOf(row), row])), [ledgers]);
  const amount = amountOk(form.amount) ? Number(form.amount) : 0;
  const same = form.debitLedgerId && form.debitLedgerId === form.creditLedgerId;
  const clientErrors = {
    voucherDate: !form.voucherDate ? "Date is required" : form.voucherDate > todayInput() ? "Date cannot be in the future" : "",
    debitLedgerId: !form.debitLedgerId ? "Select a ledger" : same ? "Debit and credit ledgers must be different" : "",
    creditLedgerId: !form.creditLedgerId ? "Select a ledger" : same ? "Debit and credit ledgers must be different" : "",
    amount: !String(form.amount).trim() ? "Enter an amount" : !amountOk(form.amount) ? "Enter an amount greater than 0 with at most 2 decimals" : "",
  };
  const valid = Object.values(clientErrors).every((message) => !message);
  const show = (key) => (touched ? clientErrors[key] : "") || "";
  const locked = Boolean(saved);
  const customerId = [form.debitLedgerId, form.creditLedgerId].find((id) => byId.get(id)?.group === "SUNDRY_DEBTOR");

  const save = async (event) => {
    event.preventDefault();
    setTouched(true);
    setError(null);
    if (!valid) return;
    setSaving(true);
    try {
      const result = await accountsFlowApi.postVoucher({
        voucherType,
        voucherDate: form.voucherDate,
        referenceNo: form.referenceNo.trim(),
        narration: form.narration.trim(),
        lines: [
          { ledgerId: form.debitLedgerId, debit: amount.toFixed(2), credit: "0" },
          { ledgerId: form.creditLedgerId, debit: "0", credit: amount.toFixed(2) },
        ],
      });
      setSaved(result.data);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (flowMeta && !flowMeta.canApprove) {
    return <PageShell title={config.title}><div className="accounts-note warn">Only {flowMeta.approverRoles.join(" or ")} users can post a {config.title.toLowerCase()}.</div></PageShell>;
  }

  return (
    <PageShell title={config.title} subtitle={`${config.hint} Double-entry, posted on save.`}>
      {saved && (
        <div className="accounts-success">
          <span>
            Voucher <strong>{saved.voucherNo}</strong> posted for {formatINR(saved.totalDebit)}.
            {(saved.oldCreditApplied || []).map((row) => ` Order credit for ${row.customerCode}: outstanding ${row.delta > 0 ? "+" : ""}${formatINR(row.delta)}.`).join("")}
          </span>
          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${saved.id || saved._id}`)}><i className="bi bi-eye" /> View</button>
          {customerId && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => navigate(`${AccountsFlowConstants.LEDGER_STATEMENT}?ledgerId=${customerId}`)}>Ledger statement</button>}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setForm(blank()); setSaved(null); setTouched(false); }}>New {config.title.toLowerCase()}</button>
        </div>
      )}
      <ApiAlert error={error} />
      <form onSubmit={save} noValidate>
        <div className="accounts-entry">
        <CollapsibleSection title="Voucher">
        <div className="accounts-panel">
          <div className="accounts-entry-grid">
            <label className="tally-field">Voucher No
              <input className="form-control" readOnly value={saved?.voucherNo || ""} placeholder="Auto-generated on save" />
            </label>
            <label className="tally-field">Date *
              <input className={`form-control${show("voucherDate") ? " is-invalid" : ""}`} type="date" max={todayInput()} value={form.voucherDate} onChange={set("voucherDate")} disabled={locked} />
              {show("voucherDate") && <span className="accounts-hint tally-error">{show("voucherDate")}</span>}
            </label>
            <label className="tally-field">Reference No
              <input className="form-control" maxLength={50} value={form.referenceNo} onChange={set("referenceNo")} disabled={locked} placeholder={voucherType === "PURCHASE" ? "Supplier bill number" : "Invoice or reference number"} />
            </label>
            <LedgerCombo label={config.debit.label} required disabled={locked} groups={meta?.ledgerGroups || []} ledgers={ledgers.filter((row) => fits(row, config.debit))}
              value={form.debitLedgerId} onChange={(id) => setForm((current) => ({ ...current, debitLedgerId: id }))} error={show("debitLedgerId")} />
            <LedgerCombo label={config.credit.label} required disabled={locked} groups={meta?.ledgerGroups || []} ledgers={ledgers.filter((row) => fits(row, config.credit))}
              value={form.creditLedgerId} onChange={(id) => setForm((current) => ({ ...current, creditLedgerId: id }))} error={show("creditLedgerId")} />
            <label className="tally-field">Amount (₹) *
              <input className={`form-control${show("amount") ? " is-invalid" : ""}`} value={form.amount} placeholder="0.00" onChange={set("amount")} disabled={locked} inputMode="decimal" />
              <span className="accounts-hint">{show("amount") || (amount ? formatINR(amount) : " ")}</span>
            </label>
            <label className="tally-field accounts-span">Narration
              <textarea className="form-control" rows={2} maxLength={500} value={form.narration} placeholder="What this voucher is for" onChange={set("narration")} disabled={locked} />
              <span className="accounts-hint">{form.narration.length}/500</span>
            </label>
          </div>
        </div>
        </CollapsibleSection>
        <CollapsibleSection title="Accounting entry preview">
        <div className="accounts-panel">
          <EntryPreview lines={[
            { ledgerId: form.debitLedgerId, ledgerName: byId.get(form.debitLedgerId)?.name || "", debit: amount, credit: 0 },
            { ledgerId: form.creditLedgerId, ledgerName: byId.get(form.creditLedgerId)?.name || "", debit: 0, credit: amount },
          ]} />
        </div>
        </CollapsibleSection>
        </div>
        {!locked && (
          <div className="accounts-form-actions">
            <button className="btn btn-outline-secondary" type="button" onClick={() => setForm(blank())} disabled={saving}>Clear</button>
            <button className="btn tally-btn" type="submit" disabled={saving}>{saving ? "Saving…" : `Save ${config.title.toLowerCase()}`}</button>
          </div>
        )}
      </form>
    </PageShell>
  );
}
